import { Column, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { AmountRow } from '~/commonComponents';
import type { IJob } from '~/models';
import { CancelShiftDialogFrame } from './CancelShiftDialogFrame';

/** Legacy copy, verbatim: it is the office's cancellation policy, so it must not be reworded. */
const SETTLEMENT_NOTE = 'Please allow 5-7 business days for funds to post to your account.';

const POLICY_NOTE =
  'For more details about our Cancellation Policy, please refer to our FAQs page.';

export interface ICancelShiftRefundDialogProps {
  job: Pick<
    IJob,
    'isOnsiteInterview' | 'local_date' | 'total' | 'cancellation_fee' | 'total_refund'
  >;
  isPending?: boolean;
  /** Also runs on Escape. */
  onClose: () => void;
  /** The caller owns the mutation and the redirect. */
  onConfirm: () => void;
}

/** v3 refund popup (GOTU-15983). Mount only while open: the frame is always `open`. */
export const CancelShiftRefundDialog = ({
  job,
  isPending = false,
  onClose,
  onConfirm,
}: ICancelShiftRefundDialogProps) => {
  const { isOnsiteInterview } = job;

  return (
    <CancelShiftDialogFrame
      title={isOnsiteInterview ? 'Cancel Onsite Interview' : 'Cancel Shift'}
      description={
        isOnsiteInterview ? (
          <>
            Are you sure you want to cancel this onsite interview on{' '}
            <strong>{job.local_date}</strong>? Please note: This interview will not be available to
            the community anymore.
          </>
        ) : (
          <>
            Are you sure you want to cancel this shift on <strong>{job.local_date}</strong>? Please
            note: This shift will not be available to the community anymore.
          </>
        )
      }
      confirmLabel={isOnsiteInterview ? 'Cancel Interview' : 'Cancel Shift'}
      isPending={isPending}
      onClose={onClose}
      onConfirm={onConfirm}
      data-cy="cancel-shift-refund-dialog"
      goBackDataCy="cancel-scheduled-go-back"
      confirmDataCy="confirm-cancel-scheduled"
    >
      <Column width="100%" data-testid="cancel-scheduled-breakdown">
        <AmountRow
          label={isOnsiteInterview ? 'Interview Charge:' : 'Shift Charge:'}
          amount={job.total}
          divider
        />
        {/* Falsy, not `!== 0`: a missing fee would otherwise print "-$0.00". */}
        {job.cancellation_fee ? (
          <AmountRow
            label={
              <Typography as="span" variant="body2" bold color={colors.error[500]}>
                Cancellation Fee:
              </Typography>
            }
            amount={job.cancellation_fee}
            variant="credit"
            divider
          />
        ) : null}
        {/* Not `variant="total"`: that label is green, which is for money the office earns. */}
        <AmountRow
          label={
            <Typography as="span" variant="body2" bold>
              Total Refund:
            </Typography>
          }
          amount={job.total_refund}
          divider
        />
      </Column>
      <Column gap={2}>
        <Typography variant="body2" as="p" textAlign="center" color={colors.text.secondary}>
          {SETTLEMENT_NOTE}
        </Typography>
        <Typography variant="body2" as="p" textAlign="center" color={colors.text.secondary}>
          {POLICY_NOTE}
        </Typography>
      </Column>
    </CancelShiftDialogFrame>
  );
};
